import React, { useMemo } from 'react';
import type { UnitDefinition, SeatSlot } from '@/types/seating-v2.types';
import { SeatChip } from './SeatChip';
import { TableShape } from './TableShape';

interface UnitRendererProps {
  unitIndex: number;
  label: string;
  unit: UnitDefinition;
  seats: SeatSlot[];
  selectedSeat: number | null;
  onSeatClick: (globalSeatNumber: number) => void;
  onSeatDrop: (globalSeatNumber: number, guestId: number) => void;
}

/**
 * Renders ONE repeatable unit: a table shape + seats arranged around it.
 * This is the ONLY component that contains seat positioning geometry.
 * Arrangement components just grid/stack/wrap these units.
 */
export const UnitRenderer: React.FC<UnitRendererProps> = (props) => {
  const { label, unit, seats, selectedSeat, onSeatClick, onSeatDrop } = props;
  const seatCount = seats.length;

  // ── Dynamic sizing: table + orbit scale with seat count ──
  const { tableW, tableH, orbitR, cellSize } = useMemo(() => {
    const DEG = Math.PI / 180;
    const SEAT_D = unit.seatRadius * 2;       // seat diameter
    const DESIRED_GAP = 6;                     // desired edge-to-edge gap between adjacent seats
    const SEAT_TABLE_GAP = 4;                  // gap between seat edge and table edge
    const MIN_TABLE_R = 14;                    // minimum table radius (very small tables OK)

    if (unit.tableShape === 'none' || seatCount === 0) {
      const r = Math.max(unit.tableWidth, unit.tableHeight) / 2;
      const orbit = r + unit.seatRadius + SEAT_TABLE_GAP;
      const cell = (orbit + unit.seatRadius) * 2 + 12;
      return { tableW: unit.tableWidth, tableH: unit.tableHeight, orbitR: orbit, cellSize: cell };
    }

    // For circular / arc placements, compute orbit purely from seat geometry
    if (unit.seatPlacement === 'full-circle' || unit.seatPlacement === 'arc') {
      const arcDeg = unit.seatPlacement === 'full-circle' ? 360 : unit.seatArc;
      // Number of gaps: full circle → seatCount, arc → seatCount+1
      const gaps = unit.seatPlacement === 'full-circle' ? seatCount : seatCount + 1;
      // Orbit radius so that seats are evenly spaced with DESIRED_GAP between edges
      // arc_length = orbit * arcRad   and   arc_length = gaps * (seatDiameter + gap)
      const arcRad = arcDeg * DEG;
      const orbit = gaps * (SEAT_D + DESIRED_GAP) / (2 * Math.PI) * (2 * Math.PI / arcRad);
      // Table fills the inner area — leave a visible gap to seats
      // Use 55% of orbit as table radius so table is clearly smaller than seat ring
      let tableR = (orbit - unit.seatRadius) * 0.55;
      tableR = Math.max(tableR, MIN_TABLE_R);
      const diameter = Math.round(tableR * 2);
      const cell = (orbit + unit.seatRadius) * 2 + 12;

      if (unit.tableShape === 'round') {
        return { tableW: diameter, tableH: diameter, orbitR: orbit, cellSize: cell };
      }
      const scale = diameter / Math.max(unit.tableWidth, unit.tableHeight);
      return { tableW: Math.round(unit.tableWidth * scale), tableH: Math.round(unit.tableHeight * scale), orbitR: orbit, cellSize: cell };
    }

    // one-side / both-sides / perimeter → keep preset table size
    const tableR = Math.max(unit.tableWidth, unit.tableHeight) / 2;
    const orbit = tableR + unit.seatRadius + SEAT_TABLE_GAP;
    const cell = (orbit + unit.seatRadius) * 2 + 12;
    return { tableW: unit.tableWidth, tableH: unit.tableHeight, orbitR: orbit, cellSize: cell };
  }, [unit, seatCount]);

  // Compute seat positions relative to center of cell
  const seatPositions = useMemo(
    () => calculateSeatPositions(unit, seatCount, orbitR),
    [unit, seatCount, orbitR]
  );

  // If no table (theater), just render a seat chip
  if (unit.tableShape === 'none') {
    return (
      <SeatChip
        seat={seats[0]}
        radius={unit.seatRadius}
        isSelected={selectedSeat === seats[0]?.globalSeatNumber}
        onSeatClick={onSeatClick}
        onSeatDrop={onSeatDrop}
      />
    );
  }

  return (
    <div
      className="relative flex-shrink-0"
      style={{ width: cellSize, height: cellSize }}
    >
      {/* Table in center */}
      <div className="absolute inset-0 flex items-center justify-center">
        <TableShape
          shape={unit.tableShape}
          width={tableW}
          height={tableH}
          label={`${label}`}
        />
      </div>

      {/* Seats positioned around table */}
      {seats.map((seat, i) => {
        const pos = seatPositions[i];
        if (!pos) return null;
        return (
          <div
            key={seat.globalSeatNumber}
            className="absolute"
            style={{
              left: cellSize / 2 + pos.x - unit.seatRadius,
              top: cellSize / 2 + pos.y - unit.seatRadius,
            }}
          >
            <SeatChip
              seat={seat}
              radius={unit.seatRadius}
              isSelected={selectedSeat === seat.globalSeatNumber}
              onSeatClick={onSeatClick}
              onSeatDrop={onSeatDrop}
            />
          </div>
        );
      })}
    </div>
  );
};

// ═══════════════════════════════════════════
// SEAT POSITION CALCULATOR
// All trig math is isolated here.
// ═══════════════════════════════════════════

function calculateSeatPositions(
  unit: UnitDefinition,
  seatCount: number,
  orbitRadius: number
): { x: number; y: number }[] {
  const DEG = Math.PI / 180;
  const positions: { x: number; y: number }[] = [];

  switch (unit.seatPlacement) {
    case 'full-circle': {
      for (let i = 0; i < seatCount; i++) {
        const angle = (unit.seatArcStart + i * (360 / seatCount)) * DEG;
        positions.push({
          x: Math.cos(angle) * orbitRadius,
          y: Math.sin(angle) * orbitRadius,
        });
      }
      break;
    }

    case 'arc': {
      const arcRad = unit.seatArc * DEG;
      const startRad = (unit.seatArcStart - unit.seatArc / 2) * DEG;
      for (let i = 0; i < seatCount; i++) {
        const angle = startRad + ((i + 1) * arcRad) / (seatCount + 1);
        positions.push({
          x: Math.cos(angle) * orbitRadius,
          y: Math.sin(angle) * orbitRadius,
        });
      }
      break;
    }

    case 'one-side': {
      // Seats in a horizontal line below (or above) the table
      const sideAngle = unit.seatArcStart * DEG;
      const distance = unit.tableHeight / 2 + unit.seatRadius + unit.seatGap;
      const seatSpacing = unit.seatRadius * 2 + 4;
      const totalWidth = (seatCount - 1) * seatSpacing;

      for (let i = 0; i < seatCount; i++) {
        const offset = -totalWidth / 2 + i * seatSpacing;
        // For seatArcStart=90 (below), perpendicular is (0, 1), tangent is (1, 0)
        const perpX = Math.cos(sideAngle);
        const perpY = Math.sin(sideAngle);
        const tangentX = -perpY;
        const tangentY = perpX;
        positions.push({
          x: perpX * distance + tangentX * offset,
          y: perpY * distance + tangentY * offset,
        });
      }
      break;
    }

    case 'both-sides': {
      const halfCount = Math.ceil(seatCount / 2);
      const seatSpacing = unit.seatRadius * 2 + 4;
      const yOffset = unit.tableHeight / 2 + unit.seatRadius + unit.seatGap;

      for (let i = 0; i < seatCount; i++) {
        const side = i < halfCount ? -1 : 1;
        const idx = i < halfCount ? i : i - halfCount;
        const rowCount = i < halfCount ? halfCount : seatCount - halfCount;
        const rowWidth = (rowCount - 1) * seatSpacing;
        positions.push({
          x: -rowWidth / 2 + idx * seatSpacing,
          y: side * yOffset,
        });
      }
      break;
    }

    case 'perimeter': {
      // Seats around the perimeter of a rectangle
      const halfW = unit.tableWidth / 2 + unit.seatRadius + unit.seatGap;
      const halfH = unit.tableHeight / 2 + unit.seatRadius + unit.seatGap;

      // Distribute seats along the perimeter
      const perim = 2 * (2 * halfW + 2 * halfH);
      const step = perim / seatCount;

      for (let i = 0; i < seatCount; i++) {
        const d = i * step;
        const topLen = 2 * halfW;
        const rightLen = 2 * halfH;

        let x: number, y: number;
        if (d < topLen) {
          // Top edge: left to right
          x = -halfW + d;
          y = -halfH;
        } else if (d < topLen + rightLen) {
          // Right edge: top to bottom
          x = halfW;
          y = -halfH + (d - topLen);
        } else if (d < 2 * topLen + rightLen) {
          // Bottom edge: right to left
          x = halfW - (d - topLen - rightLen);
          y = halfH;
        } else {
          // Left edge: bottom to top
          x = -halfW;
          y = halfH - (d - 2 * topLen - rightLen);
        }
        positions.push({ x, y });
      }
      break;
    }

    default:
      break;
  }

  return positions;
}

export default UnitRenderer;
